import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import MessageFile from 'components/MessageFiles/components/MessageFile';
import dataRoomApi from '../../dataRoomApi';
import { QuestionFile } from '../../models/QAndA';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';

import './QuestionFiles.styles.scss';

type QuestionFilesProps = {
  className?: string;
  questionId: string;
  files: QuestionFile[];
  updateQuestion: (files: QuestionFile[]) => void;
};

const QuestionFiles: React.FC<QuestionFilesProps> = (props) => {
  const { className, questionId, files, updateQuestion } = props;

  const {
    dataRoom: { _id: dataRoomId, name: dataRoomName },
  } = useContext(DataRoomContext);

  const classes = classNames('question-files', className);

  const onFilePreview = useCallback(
    (fileId: string) =>
      dataRoomApi.questionsAndAnswers.previewQuestionFile(questionId, fileId),
    [questionId],
  );

  const onFileRemove = useCallback(
    async (fileId: string, removeFromDataRoom?: boolean) => {
      await dataRoomApi.questionsAndAnswers.removeQuestionFile(
        questionId,
        fileId,
        removeFromDataRoom,
      );
      updateQuestion(files.filter((el) => el._id !== fileId));
    },
    [files, questionId, updateQuestion],
  );

  const onFileAddToDataRoom = useCallback(
    async (fileId: string) => {
      await dataRoomApi.questionsAndAnswers.moveQuestionFileToDataRoom(
        questionId,
        fileId,
      );
      updateQuestion(
        files.map((el) =>
          el._id === fileId ? { ...el, isDataRoomFile: true } : el,
        ),
      );
    },
    [files, questionId, updateQuestion],
  );

  if (!files?.length) return null;

  return (
    <div className={classes}>
      {files.map(
        ({
          _id,
          isDataRoomFile,
          canMoveFileToDataRoom,
          file: { name, type, url, originalType },
        }) => (
          <MessageFile
            key={_id}
            id={_id}
            name={name}
            url={url}
            dataRoomId={dataRoomId}
            dataRoomName={dataRoomName}
            type={originalType || type}
            isDataRoomFile={isDataRoomFile}
            canMoveFileToDataRoom={canMoveFileToDataRoom}
            canRemoveFile={canMoveFileToDataRoom}
            onFilePreview={onFilePreview}
            onFileAddToDataRoom={onFileAddToDataRoom}
            onFileRemove={onFileRemove}
          />
        ),
      )}
    </div>
  );
};

export default QuestionFiles;
